import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, effect, inject, input, linkedSignal, signal, untracked } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { uniq } from 'lodash-es';
import { DateTime } from 'luxon';
import { catchError, forkJoin, of, startWith, switchMap, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { FakeLinkDirective } from '../../directive/fake-link.directive';
import { TitleDirective } from '../../directive/title.directive';
import { setUserForm, userForm, UserFormComponent } from '../../form/user/user.component';
import { HasChanges } from '../../guard/pending-changes.guard';
import { getRole, Profile } from '../../model/profile';
import { Ref } from '../../model/ref';
import { Role, User } from '../../model/user';
import { isDeletorTag, tagDeleteNotice } from '../../mods/delete';
import { cronPlugin } from '../../mods/system/script';
import { AdminService } from '../../service/admin.service';
import { ExtService } from '../../service/api/ext.service';
import { ProfileService } from '../../service/api/profile.service';
import { UserService } from '../../service/api/user.service';
import { AuthzService } from '../../service/authz.service';
import { ConfigService } from '../../service/config.service';
import { Store } from '../../store/store';
import { downloadRef, downloadTag } from '../../util/download';
import { controlState, scrollToFirstInvalid } from '../../util/form';
import { printError } from '../../util/http';
import { localTag, subOrigin, tagOrigin } from '../../util/tag';
import { ConfirmActionComponent } from '../action/confirm-action/confirm-action.component';
import { InlineButtonComponent } from '../action/inline-button/inline-button.component';
import { InlinePasswordComponent } from '../action/inline-password/inline-password.component';
import { InlineSelectComponent } from '../action/inline-select/inline-select.component';

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss'],
  host: {
    'class': 'profile list-item',
    'tabindex': '0',
    '[class.deleted]': 'deleted()',
    '[class.editing]': 'editing()',
  },
  imports: [FakeLinkDirective, RouterLink, TitleDirective, ConfirmActionComponent, InlineButtonComponent, InlinePasswordComponent, InlineSelectComponent, ReactiveFormsModule, UserFormComponent]
})
export class UserComponent implements HasChanges {
  admin = inject(AdminService);
  config = inject(ConfigService);
  store = inject(Store);
  private auth = inject(AuthzService);
  private profiles = inject(ProfileService);
  private users = inject(UserService);
  private exts = inject(ExtService);
  private fb = inject(FormBuilder);


  readonly profileInput = input<Profile | undefined>(undefined, { alias: 'profile' });
  readonly userInput = input<User | undefined>(undefined, { alias: 'user' });
  readonly user = linkedSignal(() => this.userInput());
  private readonly profileTag = computed(() => !this.profileInput() && this.user()?.modified
    ? this.user()!.tag + (this.user()!.origin || '') : undefined);
  private readonly loadedProfile = toSignal(toObservable(this.profileTag).pipe(
    switchMap(tag => tag ? this.profiles.getProfile(tag).pipe(
      catchError(() => of(undefined)),
      startWith(undefined),
    ) : of(undefined)),
  ), { initialValue: undefined });
  readonly profile = linkedSignal(() => this.profileInput() || this.loadedProfile());
  readonly ext = toSignal(toObservable(this.profileTag).pipe(
    switchMap(tag => tag ? this.exts.getCachedExt(this.user()!.tag, this.user()!.origin).pipe(
      catchError(() => of(undefined)),
      startWith(undefined),
    ) : of(undefined)),
  ), { initialValue: undefined });
  readonly deleted = linkedSignal(() => { this.user(); this.profileInput(); return false; });
  readonly writeAccess = computed(() => this.auth.tagWriteAccess(this.qualifiedTag()) && this.auth.hasRole(this.role()));
  readonly serverError = linkedSignal<string[]>(() => { this.user(); this.profileInput(); return []; });
  readonly externalErrors = linkedSignal<string[]>(() => { this.user(); this.profileInput(); return []; });
  readonly genKey = linkedSignal(() => { this.user(); this.profileInput(); return false; });

  editForm: UntypedFormGroup;
  protected readonly editFormValid = controlState(() => this.editForm, c => c.valid);
  protected readonly editFormDirty = controlState(() => this.editForm, c => c.dirty);
  private readonly userKey = computed(() => this.qualifiedTag());
  /** Key of the User the edit form was filled from. */
  private readonly formKey = signal('');
  /** The edit form has unsaved changes for this User, even if the editor is hidden. */
  readonly draft = computed(() => !!this.editFormDirty() && this.formKey() === this.userKey());
  readonly submitted = linkedSignal(() => { this.user(); this.profileInput(); return false; });
  readonly editing = linkedSignal(() => { this.user(); this.profileInput(); return false; });
  readonly viewSource = linkedSignal(() => { this.user(); this.profileInput(); return false; });

  constructor() {
    const fb = this.fb;

    effect(() => {
      this.userInput();
      this.profileInput();
      untracked(() => this.init());
    });
    this.editForm = userForm(fb, true);
  }

  saveChanges() {
    return !this.editing() || !this.editForm.dirty;
  }

  toggleEditing() {
    if (!this.editing()) this.syncEditor();
    this.editing.set(!this.editing());
  }

  toggleViewSource() {
    if (!this.viewSource()) this.syncEditor();
    this.viewSource.set(!this.viewSource());
  }

  /** Fills the edit form from the User, unless it holds a draft for this User. */
  private syncEditor() {
    if (this.draft()) return;
    const user = this.user();
    if (!user) return;
    setUserForm(this.fb, this.editForm, user);
    this.editForm.markAsPristine();
    this.formKey.set(this.userKey());
  }

  /** Discards the draft. */
  cancelEdit() {
    this.editForm.markAsPristine();
    this.editing.set(false);
  }

  init() {
  }

  readonly created = computed(() => {
    return this.user()?.modified;
  });
  readonly qualifiedTag = computed(() => {
    return this.profile()?.tag || (this.user()!.tag + this.user()!.origin);
  });
  readonly localTag = computed(() => {
    return localTag(this.profile()?.tag) || this.user()!.tag;
  });
  readonly origin = computed(() => {
    return tagOrigin(this.profile()?.tag) || this.user()?.origin || '';
  });

  readonly recommendedAlias = computed(() => {
    const api = new URL(this.config.api, location.href);
    const firstPath = api.pathname.split('/').filter(Boolean)[0];
    return firstPath?.startsWith('~') && firstPath.length > 1
      ? '@' + firstPath.substring(1)
      : '@' + api.hostname;
  });

  readonly local = computed(() => {
    return this.profile()?.tag || (!this.user() || this.user()?.origin === this.store.account.origin());
  });
  readonly role = computed(() => {
    return getRole(this.profile()?.role, this.user()?.role);
  });

  download() {
    if (!this.user()) {
      return downloadTag({
        tag: this.profile()!.tag,
        origin: '',
      });
    }
    const user = { ...this.user()! };
    user.modified = user.modifiedString as any;
    delete user.type;
    delete user.modifiedString;
    downloadTag(user);
  }

  readonly connectionRef = computed((): Ref => {
    const template = this.store.origins.origins().find(ref =>
      subOrigin(ref.origin, ref.plugins?.['+plugin/origin']?.local) === this.origin());
    const local = template?.plugins?.['+plugin/origin']?.remote || this.origin() || this.recommendedAlias();
    return {
      url: template?.url || new URL(this.config.api, document.baseURI).href,
      title: template?.title || local,
      tags: ['public', 'internal', '+plugin/cron', '+plugin/origin/pull', '+plugin/origin/tunnel'],
      plugins: {
        '+plugin/cron': { ...cronPlugin.defaults },
        '+plugin/origin': { remote: this.origin(), local },
        '+plugin/origin/tunnel': { remoteUser: this.qualifiedTag() },
      },
    };
  });

  connect() {
    downloadRef(this.connectionRef());
  }

  setPassword$ = (password: string) => {
    return this.profiles.changePassword({ tag: this.qualifiedTag(), password }).pipe(
      catchError((res: HttpErrorResponse) => {
        this.serverError.set(printError(res));
        return throwError(() => res);
      }),
    );
  }

  ban$ = () => {
    return this.setRole$('ROLE_BANNED');
  }

  setRole$ = (role?: Role) => {
    if (!role) return of(null);
    this.serverError.set([]);
    role = role.toUpperCase().trim() as Role;
    if (this.config.scim) {
      return this.profiles.changeRole({ tag: this.qualifiedTag(), role }).pipe(
        switchMap(() => this.profiles.getProfile(this.qualifiedTag())),
        tap(profile => {
          this.profile.set(profile);
          this.init();
        }),
        catchError((res: HttpErrorResponse) => {
          this.serverError.set(printError(res));
          return throwError(() => res);
        }),
      );
    } else {
      const user = { ...(this.user() || { tag: this.qualifiedTag() }), role };
      this.user.set(user);
      return this.users.update(user).pipe(
        tap(cursor => {
          this.user.set({ ...this.user()!, modifiedString: cursor, modified: DateTime.fromISO(cursor) });
          this.init();
        }),
        catchError((res: HttpErrorResponse) => {
          this.serverError.set(printError(res));
          return throwError(() => res);
        }),
      );
    }
  }

  activate$ = () => {
    return this.profiles.activate(this.qualifiedTag()).pipe(
      switchMap(() => this.profiles.getProfile(this.qualifiedTag())),
      tap(profile => {
        this.profile.set(profile);
        this.init();
      }),
      catchError((res: HttpErrorResponse) => {
        this.serverError.set(printError(res));
        return throwError(() => res);
      }),
    );
  }

  deactivate$ = () => {
    return this.profiles.deactivate(this.qualifiedTag()).pipe(
      switchMap(() => this.profiles.getProfile(this.qualifiedTag())),
      tap(profile => {
        this.profile.set(profile);
        this.init();
      }),
      catchError((res: HttpErrorResponse) => {
        this.serverError.set(printError(res));
        return throwError(() => res);
      }),
    );
  }

  save() {
    this.submitted.set(true);
    this.editForm.markAllAsTouched();
    if (!this.editForm.valid) {
      scrollToFirstInvalid();
      return;
    }
    const updates: User = {
      ...(this.user() || {}),
      ...this.editForm.value,
      tag: this.localTag(),
      origin: this.origin(),
      readAccess: uniq([...this.editForm.value.readAccess, ...this.editForm.value.notifications]),
    };
    this.externalErrors.set([]);
    try {
      if (!updates.external) delete updates.external;
      if (updates.external) updates.external = JSON.parse(updates.external);
    } catch (e: any) {
      this.externalErrors.update(externalErrors => [...externalErrors, e.message]);
    }
    (this.user()
      ? this.users.update(updates)
      : this.users.create(updates)).pipe(
      catchError((err: HttpErrorResponse) => {
        this.serverError.set(printError(err));
        return throwError(() => err);
      }),
    ).subscribe(cursor => {
      this.editForm.reset();
      this.user.set({ ...updates, modifiedString: cursor, modified: DateTime.fromISO(cursor) });
      this.serverError.set([]);
      this.editing.set(false);
      this.init();
    });
  }

  copy$ = () => {
    return this.users.create({
      ...this.user()!,
      origin: this.store.account.origin(),
    }).pipe(
      catchError((err: HttpErrorResponse) => {
        this.serverError.set(printError(err));
        return throwError(() => err);
      }),
    );
  }

  delete$ = () => {
    this.serverError.set([]);
    const os = [];
    if (this.user()) {
      const deleteNotice = !isDeletorTag(this.user()!.tag) && this.admin.getPlugin('plugin/delete')
        ? this.users.create(tagDeleteNotice(this.user()!))
        : of(null);
      os.push(this.users.delete(this.qualifiedTag()).pipe(
        tap(() => this.deleted.set(true)),
        switchMap(() => deleteNotice),
        catchError((err: HttpErrorResponse) => {
          this.serverError.set(printError(err));
          return throwError(() => err);
        }),
      ));
    }
    if (this.profile()) {
      os.push(this.profiles.delete(this.qualifiedTag()).pipe(
        catchError((err: HttpErrorResponse) => {
          this.serverError.update(errors => [...errors, ...printError(err)]);
          return throwError(() => err);
        }),
      ));
    }
    return forkJoin(os).pipe(
      tap(() => this.deleted.set(true)),
    );
  }

  keygen$ = () => {
    this.serverError.set([]);
    return this.users.keygen(this.qualifiedTag()).pipe(
      catchError((err: HttpErrorResponse) => {
        this.serverError.update(errors => [...errors, ...printError(err)]);
        return throwError(() => err);
      }),
      switchMap(() => this.users.get(this.qualifiedTag())),
      tap(user => {
        this.user.set(user);
        this.serverError.set([]);
        this.genKey.set(false);
        this.init();
      })
    );
  }
}
